import { apiService } from '../../services/api';
import type {
  ApplyResult,
  ApplyUpdateInput,
  ChangeStamp,
  HeadChoice,
  CountryInput,
  CountryProfile,
  GovNodeView,
  GovOfficeHolder,
  GovUpdateView,
  HealthView,
  NameChangeView,
  NewNodeInput,
  NodePatch,
  NodePlan,
  RenameInput,
  RunView,
  StartRunInput,
  StructureView,
  TimelineView,
  UpdateCounts,
  UpdateQuery,
} from './types';

export class GovernmentUnavailableError extends Error {
  constructor(message = 'The government map is not available on this server yet.') {
    super(message);
    this.name = 'GovernmentUnavailableError';
  }
}

/** The response body, with 404 read as "this server does not have it yet" */
const unwrap = async <T,>(call: Promise<any>): Promise<T> => {
  try {
    return (await call).data as T;
  } catch (error: any) {
    if (error?.response?.status === 404 && !error?.response?.data?.message) {
      throw new GovernmentUnavailableError();
    }
    throw error;
  }
};

export interface Overview {
  country: CountryProfile;
  structure: StructureView['stats'];
  updates: UpdateCounts;
  activeRun: RunView | null;
  runs: RunView[];
  researchReady: boolean;
  matchedAt: string | null;
}

export const fetchOverview = () => unwrap<Overview>(apiService.get('/api/government/overview'));

export const fetchCountry = () => unwrap<CountryProfile>(apiService.get('/api/government/country'));

export const fetchCountryOptions = () =>
  unwrap<Array<{ code: string; name: string; languages: string[] }>>(
    apiService.get('/api/government/country/options'),
  );

export const saveCountry = (input: CountryInput) =>
  unwrap<CountryProfile>(apiService.put('/api/government/country', input));

/* ── Structure ──────────────────────────────────────────────────── */

export const fetchStructure = () => unwrap<StructureView>(apiService.get('/api/government/structure'));

export const patchNode = (id: string, patch: NodePatch) =>
  unwrap<GovNodeView>(apiService.patch(`/api/government/structure/nodes/${id}`, patch));

export const addNode = (input: NewNodeInput) =>
  unwrap<GovNodeView>(apiService.post('/api/government/structure/nodes', input));

export const setNodeStatus = (ids: string[], status: 'proposed' | 'confirmed' | 'rejected') =>
  unwrap<{ changed: number }>(apiService.post('/api/government/structure/status', { ids, status }));

export interface MergePreview {
  survivor: { id: string; label: string } | null;
  merges: Array<{ id: string; label: string; moved: Record<string, number>; warnings: string[] }>;
}

export const previewNode = (id: string) =>
  unwrap<MergePreview>(apiService.get(`/api/government/structure/nodes/${id}/preview`));

/** Who currently holds a post at each mapped entity, most senior first */
export const fetchOfficeHolders = () =>
  unwrap<GovOfficeHolder[]>(apiService.get('/api/government/structure/people'));

/** What applying these entities would do, worked out before anything is written */
export const planNodes = (ids: string[]) =>
  unwrap<{ plans: NodePlan[] }>(apiService.post('/api/government/structure/plan', { ids }));

export const applyNodes = (ids: string[], heads?: Record<string, HeadChoice>) =>
  unwrap<{ results: ApplyResult[] }>(apiService.post('/api/government/structure/apply', { ids, heads }));

/** Moves whenever anything the government page shows has changed */
export const fetchChanges = () => unwrap<ChangeStamp>(apiService.get('/api/government/changes'));


export const resolveOutside = (
  organizationId: string,
  action: 'detach' | 'move' | 'keep',
  targetNodeId?: string | null,
) =>
  unwrap<{ ok: true }>(
    apiService.post(`/api/government/structure/outside/${organizationId}`, {
      action,
      targetNodeId: targetNodeId ?? null,
    }),
  );

/* ── Runs ───────────────────────────────────────────────────────── */

export const startRun = (input: StartRunInput) =>
  unwrap<RunView>(apiService.post('/api/government/runs', input));

export const fetchRuns = (limit?: number) =>
  unwrap<RunView[]>(apiService.get('/api/government/runs', { params: { limit } }));

export const fetchRun = (id: string, after = 0) =>
  unwrap<RunView>(apiService.get(`/api/government/runs/${id}`, { params: { after } }));

/** Start the work of a run that stopped short, keeping what it had finished */
export const resumeRun = (id: string) =>
  unwrap<RunView>(apiService.post(`/api/government/runs/${id}/resume`, {}));

/** What is wrong with the directory's government right now */
export const fetchHealth = () => unwrap<HealthView>(apiService.get('/api/government/health'));

/** The acts on file, and who held each post from when to when */
export const fetchTimeline = () => unwrap<TimelineView>(apiService.get('/api/government/timeline'));

export const cancelRun = (id: string) =>
  unwrap<{ stopping: boolean }>(apiService.post(`/api/government/runs/${id}/cancel`, {}));

/* ── Updates ────────────────────────────────────────────────────── */

export const fetchUpdates = (query: UpdateQuery = {}) =>
  unwrap<GovUpdateView[]>(apiService.get('/api/government/updates', { params: query }));

export const fetchUpdateCounts = () =>
  unwrap<UpdateCounts>(apiService.get('/api/government/updates/counts'));

export const applyUpdate = (id: string, input: ApplyUpdateInput = {}) =>
  unwrap<GovUpdateView>(apiService.post(`/api/government/updates/${id}/apply`, input));

export const dismissUpdate = (id: string, reason?: string) =>
  unwrap<GovUpdateView>(apiService.post(`/api/government/updates/${id}/dismiss`, { reason }));

export const verifyUpdate = (id: string) =>
  unwrap<RunView>(apiService.post(`/api/government/updates/${id}/verify`, {}));

/* ── An organization's names over time ──────────────────────────── */

export const fetchNameHistory = (organizationId: string) =>
  unwrap<NameChangeView[]>(apiService.get(`/api/organizations/${organizationId}/names`));

export const renameOrganization = (organizationId: string, input: RenameInput) =>
  unwrap<{
    organization: { id: string; name: string; acronym: string | null; aliases: string[] };
    change: NameChangeView | null;
  }>(apiService.post(`/api/organizations/${organizationId}/rename`, input));
